import type { AdminRole, LoginResponse, SafeUser } from '@diora/contracts';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  apiPost,
  refreshSession,
  setAccessToken,
  setPasswordChangeRequiredHandler,
  setSessionLostHandler,
} from '@/lib/api';

interface AuthContextValue {
  user: SafeUser | null;
  /** True until the silent refresh on mount has settled. */
  initializing: boolean;
  signIn: (email: string, password: string) => Promise<SafeUser>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Role check. The server enforces this too — this only shapes the UI. */
  can: (...roles: AdminRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SafeUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  /**
   * Restore the session on load.
   *
   * The access token is deliberately in memory, so a reload loses it. The
   * refresh cookie is not — so a reload is a refresh, and a person who left the
   * tab open yesterday does not get bounced to a login form for no reason.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        // Shared with the 401 interceptor, so a double-invoked effect in
        // StrictMode makes one request rather than two — see refreshSession.
        const session = await refreshSession();
        if (cancelled) return;
        setUser(session.user);
      } catch {
        // No cookie, or it was rejected. Not an error worth showing — it is
        // simply what "not signed in" looks like.
        if (!cancelled) setAccessToken(null);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // When a refresh fails mid-session, the interceptor calls this rather than
  // letting every in-flight query render its own "unauthenticated" error.
  useEffect(() => {
    setSessionLostHandler(() => setUser(null));

    // An Owner can set `mustChangePassword` on an account whose dashboard is
    // already open. The session stays valid, so signing the person out would be
    // wrong; flipping the flag they already hold sends them to the one screen
    // that clears it, which is what `App.tsx` already does with it on sign-in.
    setPasswordChangeRequiredHandler(() =>
      setUser((current) => (current ? { ...current, mustChangePassword: true } : current)),
    );
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data } = await apiPost<LoginResponse>('/admin/auth/login', { email, password });
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      // Revokes the refresh family server-side. A failure here still signs the
      // person out locally — refusing to let someone leave is not an option.
      await apiPost('/admin/auth/logout');
    } catch {
      /* already gone */
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await apiPost('/admin/auth/change-password', { currentPassword, newPassword });

    /*
     * The change ends this session, deliberately and server-side.
     *
     * `AuthService.changePassword` revokes every refresh token the account has,
     * this one included — if the password was changed because it leaked, leaving
     * the old sessions alive defeats the point — and the controller clears the
     * cookie on the way out.
     *
     * This used to call `refreshSession()` here to re-read the row. That asks
     * the API to rotate a cookie it has just revoked and cleared, so it answered
     * 401 every single time: the password had been changed, and the screen
     * reported failure. Signing out locally is what the server already did.
     */
    setAccessToken(null);
    setUser(null);
  }, []);

  const can = useCallback(
    (...roles: AdminRole[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, initializing, signIn, signOut, changePassword, can }),
    [user, initializing, signIn, signOut, changePassword, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth used outside AuthProvider');
  return value;
}
